import { ChangeEvent, Component, ElementType, cloneElement } from "react";

import { checkCredentials } from "../api.js";
import { trns } from "../helper.js";
import * as cons from "../constants.js";
import { StringMap, Nullable } from "../types/types.js";
import useRegStore from "../store/index.js";

export interface StateSubmit {
  email?: string;
  login?: string;
  [rest: string]: any;
}

interface ExportParams {
  [key: string]: any;
}

type Props = {
  required: StringMap<boolean>;
  optional: StringMap<boolean>;
  saveValues: (state: StateSubmit, errors?: StringMap<string> | null, e?: any) => void;
  nextPage: () => void;
  saved: StringMap<string | Nullable<boolean>>;
  setDirty: (field: string) => void;
  dirty: StringMap<boolean>;
  errors: StringMap<string>;
  clearError: (x: string) => void;
  servicePath: string;
  [rest: string]: any;
};

export default function withStateSubmit(
  Wrapped: ElementType,
  initial: StateSubmit,
  step: string,
  refs: any
) {
  return class Export extends Component<Props, StateSubmit> implements ExportParams {
    [k: string]: any;
    constructor(props: Props) {
      super(props);
      this.onSubmit = this.onSubmit.bind(this);
      this.handleField = this.handleField.bind(this);
      this.changeState = this.changeState.bind(this);
      this.reqOrOpt = this.reqOrOpt.bind(this);
      this.req = this.req.bind(this);
      this.credentials = this.credentials.bind(this);
      this.state = initial;
    }

    componentDidMount() {
      // load previously saved values;
      const { saved } = this.props;
      Object.keys(saved).forEach(x => {
        if (saved[x]) {
          this.setState({ [x]: saved[x] });
        }
      });
    }

    componentDidUpdate() {
      const { errors } = this.props;

      if (errors) {
        Object.keys(errors).forEach(x => {
          const refN = `${x}_ref`;
          if (this[refN]) {
            this[refN].setError(errors[x]);
            this.props.clearError(x);
          }
        });
      }
    }

    componentWillUnmount() {
      this.props.saveValues({ [step as keyof StateSubmit]: this.state });
    }

    async credentials() {
      const post = {
        email: this.state.email,
        login: this.state.login,
        client: "checkCredentials",
      };

      return await checkCredentials(post, this.props.servicePath);
      // RESPONSE: {"login":{"value":"test","available":true},"email":{"value":"test@test.com","available":false}}
    }

    async onSubmit(e: KeyboardEvent | MouseEvent, newState: StringMap<string> = {}) {
      e.preventDefault();
      const fields = Object.keys(this.state).filter(f => this.req(f));
      const errors = fields.filter(name => {
        const refName = `${name}_ref`;

        if (this[refName]) {
          return this[refName].state.error === null || this[refName].state.ok !== true;
        } else {
          return false;
        }
      });

      if (step === "step1") {
        if (errors.length > 0) {
          errors.forEach(name => {
            const refName = `${name}_ref`;
            this[refName].getValidationState(this.state[name]);
          });
        } else {
          const errs = await this.credentials();

          if (errs) {
            if ("code" in errs && errs.code === "MISSING_LOGIN") {
              this.props.saveValues({ [step as keyof StateSubmit]: this.state });
            }

            if ("login" in errs && !errs.login.available) {
              if (errs.login.error === cons.LOGIN_NA) {
                this.props.saveValues(
                  {
                    [step as keyof StateSubmit]: this.state,
                    ...(errs.login.alternatives && { suggestions: errs.login.alternatives }),
                  },
                  {
                    login: trns(
                      "IDS_LOGINBOX_LOGIN_IN_USE",
                      "The login you have chose is already in use."
                    ),
                  }
                );
              } else if (errs.login.error === cons.INVALID_LOGIN_CHAR) {
                this.props.saveValues(
                  {
                    [step as keyof StateSubmit]: this.state,
                    ...(errs.login.alternatives && { suggestions: errs.login.alternatives }),
                  },
                  {
                    login: trns(
                      "IDS_LOGINBOX_LOGIN_INVALID_CHAR",
                      "Invalid characters, only letters, numbers and underscore/dash allowed."
                    ),
                  }
                );
              }
            }

            if ("email" in errs && !errs.email.available) {
              this.props.saveValues(
                { [step as keyof StateSubmit]: this.state },
                {
                  email: trns(
                    "IDS_LOGINBOX_EMAIL_ALREADY_REG",
                    "Your email address indicates you may already be registered"
                  ),
                }
              );
              return;
            }

            if (
              !(errs || {}).code &&
              !(((errs || {}).login || {}).available && ((errs || {}).email || {}).available)
            ) return;
          }
        }
      }

      if (errors.length > 0) {
        // Print error fields
        errors.forEach(name => {
          const refName = `${name}_ref`;
          this[refName].getValidationState(this.state[name]);
        });
      } else {
        this.setState(
          (prevState, _props) =>
            newState
              ? { ...prevState, ...this.state, ...newState }
              : { ...prevState, ...this.state },
          () => {
            this.props.saveValues({ [step as keyof StateSubmit]: this.state }, null, () => {
              this.props.nextPage();
            });
          }
        );
      }
    }

    req(fieldname: string) {
      const mode = useRegStore.getState().mode;
      const { required } = useRegStore.getState()[mode];
      return fieldname in required;
    }

    reqOrOpt(fieldname: string) {
      const mode = useRegStore.getState().mode;
      const { required, optional } = useRegStore.getState()[mode];
      return fieldname in required || fieldname in optional;
    }

    handleField(fieldname: string, v: ChangeEvent<HTMLFormElement>) {
      this.setState({ [fieldname]: v.target.value });
      if (!(fieldname in this.props.dirty)) this.props.setDirty(fieldname);
    }

    changeState(fieldname: string, val: string | boolean, setdirty: boolean = false) {
      this.setState({ [fieldname]: val }, () => {
        if (this[`${fieldname}_ref`]) {
          this[`${fieldname}_ref`].getValidationState(val);
        }
        if (setdirty && !(fieldname in this.props.dirty)) this.props.setDirty(fieldname);
      });
    }

    render() {
      return cloneElement(
        <Wrapped
          onSubmit={this.onSubmit}
          state={this.state}
          handleField={this.handleField}
          changeState={this.changeState}
          reqOrOpt={this.reqOrOpt}
          isReq={this.req}
          {...this.props}
        />,
        refs(this)
      );
    }
  };
}
